import { useState } from 'react';
import { Sparkles, CheckCircle, XCircle, HelpCircle, Plus, Loader, ArrowRightLeft, ListTodo } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Textarea from '../ui/Textarea';
import Spinner from '../ui/Spinner';
import { CompletenessGap, GapActionType, GapAnalysisResponse } from '../../types/completenessGap';

interface GapResolutionDialogProps {
  gap: CompletenessGap | null;
  isOpen: boolean;
  onClose: () => void;
  onResolve: (gapId: string, actionType: GapActionType, userInput?: string, dismissalReason?: string) => Promise<void>;
  onAnalyze?: (gapId: string, userInput?: string) => Promise<GapAnalysisResponse | null>;
  onCreateGapsFromAnalysis?: (gapId: string, analysis: any) => Promise<void>;
}

export default function GapResolutionDialog({
  gap,
  isOpen,
  onClose,
  onResolve,
  onAnalyze,
  onCreateGapsFromAnalysis,
}: GapResolutionDialogProps) {
  const [selectedAction, setSelectedAction] = useState<GapActionType | null>(null);
  const [userInput, setUserInput] = useState('');
  const [dismissalReason, setDismissalReason] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [isCreatingGaps, setIsCreatingGaps] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<GapAnalysisResponse | null>(null);

  if (!gap) return null;

  const handleAnalyze = async () => {
    if (!onAnalyze) return;

    setIsAnalyzing(true);
    try {
      const result = await onAnalyze(gap.gapId, userInput || undefined);
      setAnalysisResult(result);
    } catch (error) {
      console.error('Analysis failed:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleResolve = async () => {
    if (!selectedAction) return;

    setIsResolving(true);
    try {
      await onResolve(
        gap.gapId,
        selectedAction,
        userInput || undefined,
        dismissalReason || undefined
      );

      // Reset state and close
      setSelectedAction(null);
      setUserInput('');
      setDismissalReason('');
      setAnalysisResult(null);
      onClose();
    } catch (error) {
      console.error('Resolution failed:', error);
    } finally {
      setIsResolving(false);
    }
  };

  const detectActionType = (actionText: string): GapActionType => {
    const lowerText = actionText.toLowerCase();
    if (lowerText.includes('add entity') || lowerText.includes('create entity')) {
      return 'add_entity';
    } else if (lowerText.includes('add attribute') || lowerText.includes('create attribute')) {
      return 'add_attribute';
    } else if (lowerText.includes('add relationship') || lowerText.includes('create relationship')) {
      return 'add_relationship';
    } else {
      return 'clarify';
    }
  };

  const handleFollowAISuggestion = async () => {
    if (!gap.aiActions || gap.aiActions.length === 0) return;

    // Detect action type from first AI action
    const actionType = detectActionType(gap.aiActions[0]);

    setIsResolving(true);
    try {
      await onResolve(
        gap.gapId,
        actionType,
        gap.aiActions.join('\n'), // Pass all AI actions as context
        undefined
      );

      // Reset state and close
      setSelectedAction(null);
      setUserInput('');
      setDismissalReason('');
      setAnalysisResult(null);
      onClose();
    } catch (error) {
      console.error('Resolution failed:', error);
    } finally {
      setIsResolving(false);
    }
  };

  const handleCreateGapsFromAnalysis = async () => {
    if (!onCreateGapsFromAnalysis || !analysisResult) return;

    setIsCreatingGaps(true);
    try {
      await onCreateGapsFromAnalysis(gap.gapId, analysisResult.analysis);

      // Reset state and close
      setSelectedAction(null);
      setUserInput('');
      setDismissalReason('');
      setAnalysisResult(null);
      onClose();
    } catch (error) {
      console.error('Failed to create gaps from analysis:', error);
    } finally {
      setIsCreatingGaps(false);
    }
  };

  const handleClose = () => {
    setSelectedAction(null);
    setUserInput('');
    setDismissalReason('');
    setAnalysisResult(null);
    onClose();
  };

  const actionTypes: Array<{ type: GapActionType; label: string; icon: React.ReactNode; description: string }> = [
    {
      type: 'add_entity',
      label: 'Add Entity',
      icon: <Plus className="w-5 h-5" />,
      description: 'Add a new entity type with attributes to the data model',
    },
    {
      type: 'add_attribute',
      label: 'Add Attribute',
      icon: <Plus className="w-5 h-5" />,
      description: 'Add attributes to an existing entity',
    },
    {
      type: 'add_relationship',
      label: 'Add Relationship',
      icon: <ArrowRightLeft className="w-5 h-5" />,
      description: 'Add a new relationship type between entities',
    },
    {
      type: 'clarify',
      label: 'Clarify',
      icon: <HelpCircle className="w-5 h-5" />,
      description: 'Get more information about what\'s needed',
    },
    {
      type: 'dismiss',
      label: 'Dismiss',
      icon: <XCircle className="w-5 h-5" />,
      description: 'Mark this gap as not applicable',
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Resolve Completeness Gap"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isResolving || isCreatingGaps}>
            Cancel
          </Button>
          {!selectedAction && onAnalyze && (
            <Button
              variant="secondary"
              onClick={handleAnalyze}
              disabled={isAnalyzing}
              loading={isAnalyzing}
            >
              <Sparkles className="w-4 h-4" />
              Analyze with Amplinth
            </Button>
          )}
          {analysisResult && analysisResult.analysis.actionableSteps && analysisResult.analysis.actionableSteps.length > 1 && !selectedAction && onCreateGapsFromAnalysis && (
            <Button
              variant="primary"
              onClick={handleCreateGapsFromAnalysis}
              disabled={isCreatingGaps}
              loading={isCreatingGaps}
            >
              <ListTodo className="w-4 h-4" />
              Create {analysisResult.analysis.actionableSteps.length} Gaps from Analysis
            </Button>
          )}
          {selectedAction && (
            <Button
              variant="primary"
              onClick={handleResolve}
              disabled={isResolving || (selectedAction === 'dismiss' && !dismissalReason.trim())}
              loading={isResolving}
            >
              <CheckCircle className="w-4 h-4" />
              Apply Resolution
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-6">
        {/* Gap Description */}
        <div className="bg-neutral-50 p-4 rounded-lg border border-neutral-200">
          <h4 className="font-semibold text-neutral-900 mb-2">Gap Description</h4>
          <p className="text-sm text-neutral-700">{gap.description}</p>
          <div className="mt-3 flex items-center gap-4 text-xs text-neutral-600">
            <span>Impact: <strong className="text-primary-600">{gap.impactScore.toFixed(1)}</strong></span>
            <span>Category: <strong>{gap.category.replace('_', ' ')}</strong></span>
          </div>
        </div>

        {/* AI Actions */}
        {gap.aiActions && gap.aiActions.length > 0 && (
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-blue-600" />
                <h4 className="font-semibold text-blue-900">Amplinth-Suggested Actions</h4>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={handleFollowAISuggestion}
                disabled={isResolving}
                loading={isResolving}
              >
                <CheckCircle className="w-4 h-4" />
                Execute Amplinth Suggestion
              </Button>
            </div>
            <ul className="space-y-2">
              {gap.aiActions.map((action, index) => (
                <li key={index} className="text-sm text-blue-800 pl-4">
                  • {action}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* User Input */}
        <div>
          <Textarea
            label="Additional Context (Optional)"
            placeholder="Provide any additional context or specific requirements..."
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            rows={3}
          />
        </div>

        {/* Analysis Result */}
        {isAnalyzing && (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <Spinner size="lg" />
              <p className="text-sm text-neutral-600 mt-3">
                Analyzing this gap...
              </p>
            </div>
          </div>
        )}

        {analysisResult && (
          <div className="bg-gradient-to-br from-primary-50 to-secondary-50 p-4 rounded-lg border border-primary-200">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-primary-600" />
              <h4 className="font-semibold text-neutral-900">Analysis</h4>
            </div>

            <div className="space-y-3 text-sm">
              <div>
                <h5 className="font-medium text-neutral-900 mb-1">Summary:</h5>
                <p className="text-neutral-700">{analysisResult.analysis.aiSuggestion}</p>
              </div>

              {analysisResult.analysis.rootCause && (
                <div>
                  <h5 className="font-medium text-neutral-900 mb-1">Root Cause:</h5>
                  <p className="text-neutral-700">{analysisResult.analysis.rootCause}</p>
                </div>
              )}

              {analysisResult.analysis.actionableSteps && analysisResult.analysis.actionableSteps.length > 0 && (
                <div>
                  <h5 className="font-medium text-neutral-900 mb-1">Actionable Steps:</h5>
                  <ul className="space-y-1">
                    {analysisResult.analysis.actionableSteps.map((step, index) => (
                      <li key={index} className="text-neutral-700 pl-4">• {step}</li>
                    ))}
                  </ul>
                </div>
              )}

              {analysisResult.analysis.expectedImpact && (
                <div>
                  <h5 className="font-medium text-neutral-900 mb-1">Expected Impact:</h5>
                  <p className="text-neutral-700">{analysisResult.analysis.expectedImpact}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Type Selection */}
        <div>
          <h4 className="font-semibold text-neutral-900 mb-3">Choose Resolution Action</h4>
          <div className="grid grid-cols-2 gap-3">
            {actionTypes.map((action) => (
              <button
                key={action.type}
                onClick={() => setSelectedAction(action.type)}
                className={`p-4 rounded-lg border-2 text-left transition-all hover:shadow-md ${
                  selectedAction === action.type
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-neutral-200 bg-white hover:border-neutral-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`${
                    selectedAction === action.type ? 'text-primary-600' : 'text-neutral-500'
                  }`}>
                    {action.icon}
                  </div>
                  <span className={`font-medium ${
                    selectedAction === action.type ? 'text-primary-900' : 'text-neutral-900'
                  }`}>
                    {action.label}
                  </span>
                </div>
                <p className="text-xs text-neutral-600">{action.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Dismissal Reason */}
        {selectedAction === 'dismiss' && (
          <div>
            <Textarea
              label="Dismissal Reason (Required)"
              placeholder="Explain why this gap is being dismissed..."
              value={dismissalReason}
              onChange={(e) => setDismissalReason(e.target.value)}
              rows={3}
              required
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
